import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { format, parseISO } from "date-fns";
import Link from "next/link";
import { useState } from "react";
import ClientViewAppointmentStatusChip from "@/components/booking/clientViewAppointmentStatusChip";
import TextButton from "@/components/common/buttons/textButton";
import { ChevronSmallRightIcon } from "@/components/common/icons";
import InfoCard from "@/components/common/infoCard/infoCard";
import { DATE_FORMATS, TIME_FORMATS } from "@/config";
import { useIsOTPClientPage } from "@/hooks/client/useIsOTPClientPage";
import useMedspaTimezone from "@/hooks/common/useMedspaTimezone";
import { VisitStatus } from "@/types";
import { TelehealthIcon } from "@/utils/telehealth";
import { Visit } from "./appointmentsSection";

type VisitSubtitleDetails = {
  medspa: {
    name: string;
    id?: string;
  };
  provider: {
    firstName: string;
    lastName: string;
  };
  appointment: {
    startTime: string;
    endTime?: string;
  };
};

export default function ClientAppointmentCard({
  title,
  visits,
}: {
  title: string;
  visits: Visit[];
}) {
  const isOTPClientPage = useIsOTPClientPage();

  const PAGE_SIZE = 3;
  const [displayedVisitsCount, setDisplayedVisitsCount] = useState(PAGE_SIZE);

  const {
    palette: { gray },
  } = useTheme();

  // We can't paginate on graphql level since we need to grab all visits to display forms
  const onViewMoreClick = () => {
    setDisplayedVisitsCount((prev) =>
      Math.min(prev + PAGE_SIZE, visits.length)
    );
  };

  const getAppointmentUrl = (visit: Visit) => {
    const {
      medspa: { slug: medspaSlug },
      appointment: { accessToken: appointmentAccessToken },
    } = visit;
    return isOTPClientPage
      ? `/client/${medspaSlug}/appointments/${appointmentAccessToken}`
      : `/booking/${medspaSlug}/appointments/${appointmentAccessToken}`;
  };

  const displayShowMoreButton = visits.length > displayedVisitsCount;

  return (
    <InfoCard title={title}>
      {!visits.length && (
        <Typography
          sx={{
            color: "secondary.light",
          }}
        >
          All {title.toLocaleLowerCase()} will appear here
        </Typography>
      )}
      <Stack spacing={2} divider={<Divider />}>
        {visits.slice(0, displayedVisitsCount).map((visit) => (
          <Link key={visit.id} href={getAppointmentUrl(visit)}>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Stack>
                <Box
                  sx={{
                    mb: 0.5,
                  }}
                >
                  <ClientViewAppointmentStatusChip
                    status={visit.status as VisitStatus}
                  />
                </Box>
                <ClientAppointmentsViewCardTitle visit={visit} />
                <ClientAppointmentsViewCardSubtitle
                  visit={visit as VisitSubtitleDetails}
                />
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  ml: 3,
                }}
              >
                <ChevronSmallRightIcon color={gray.main} />
              </Box>
            </Stack>
          </Link>
        ))}
      </Stack>
      {displayShowMoreButton && (
        <TextButton onClick={onViewMoreClick} sx={{ mt: 1 }}>
          Show more
        </TextButton>
      )}
    </InfoCard>
  );
}

function ClientAppointmentsViewCardTitle({ visit }: { visit: Visit }) {
  const {
    serviceMenuItemLines,
    appointment: { startTime },
    isVisitTelehealth,
    telehealthVisitDetails,
  } = visit;

  return (
    <Stack direction="row" spacing={1}>
      {isVisitTelehealth && (
        <TelehealthIcon telehealthDetails={telehealthVisitDetails} />
      )}
      <Typography
        variant="subtitleDefault"
        sx={{
          color: "text.primary",
        }}
      >
        {serviceMenuItemLines.map((s) => s.serviceMenuItem.name).join(" • ")}
      </Typography>
      <Typography
        sx={{
          color: "secondary.main",
        }}
      >
        {format(parseISO(startTime), DATE_FORMATS.DATE_PICKER)}
      </Typography>
    </Stack>
  );
}

function ClientAppointmentsViewCardSubtitle({
  visit: {
    medspa: { id, name },
    provider: { firstName, lastName },
    appointment: { startTime, endTime },
  },
  withDateAndTimezone = false,
}: {
  visit: VisitSubtitleDetails;
  withDateAndTimezone?: boolean;
}) {
  const { medspaReadableTimezone } = useMedspaTimezone(id);

  const baseInfo = [name, `${firstName} ${lastName}`];
  const appointmentHoursRange = `${format(
    parseISO(startTime),
    TIME_FORMATS.BOOKING
  )} - ${endTime ? format(parseISO(endTime), TIME_FORMATS.BOOKING) : ""}`;

  const shortInfoToDisplay = [...baseInfo, appointmentHoursRange];

  const longInfoToDisplay = [
    ...baseInfo,
    `${format(parseISO(startTime), DATE_FORMATS.DATE_PICKER)}`,
    appointmentHoursRange,
    `(${medspaReadableTimezone})`,
  ];

  const infoToDisplay = withDateAndTimezone
    ? longInfoToDisplay
    : shortInfoToDisplay;

  return (
    <Typography
      variant="paragraphSmall"
      sx={{
        color: "secondary.main",
      }}
    >
      {infoToDisplay.join(" • ")}
    </Typography>
  );
}
